"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processLanguageMutations = processLanguageMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _selectors = require("./selectors");
var _rdfList = require("../shared/rdfList");
var _idNodeFactory = require("../shared/idNodeFactory");
var _texts = require("../../texts");
// Language entries are serialized as an ordered rdf:List of id nodes, where
// each id node points to an IANA URI via solid:publicId.
const LANGUAGE_IANA_NS = 'https://www.w3.org/ns/iana/language-code/';
function ensureLanguagePrefix(store) {
  const registerPrefix = target => {
    if (!target) return;
    if (typeof target.setPrefixForURI === 'function') {
      target.setPrefixForURI('l', LANGUAGE_IANA_NS);
      return;
    }
    if (!target.namespaces) {
      target.namespaces = {};
    }
    target.namespaces.l = LANGUAGE_IANA_NS;
  };
  const anyStore = store;
  registerPrefix(anyStore);
  registerPrefix(anyStore?.updater?.store);
}
function normalizeText(value) {
  return (value || '').trim();
}
function normalizeLanguageCode(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return '';
  if (normalized.startsWith(LANGUAGE_IANA_NS)) {
    return normalized.slice(LANGUAGE_IANA_NS.length);
  }
  return normalized;
}
function rowsFromOrderedInput(orderedRows) {
  return (orderedRows || []).filter(row => row.status !== 'deleted').filter(row => Boolean(normalizeText(row.publicId) || normalizeText(row.name) || normalizeText(row.entryNode))).map(row => ({
    ...row,
    name: normalizeText(row.name),
    publicId: normalizeText(row.publicId),
    proficiency: normalizeText(row.proficiency),
    entryNode: normalizeText(row.entryNode)
  }));
}
function buildRdfListStatements(items, doc) {
  if (!items.length) {
    return {
      head: _solidUi.ns.rdf('nil'),
      statements: []
    };
  }
  const listNodes = items.map(() => (0, _rdflib.blankNode)());
  const statements = [];
  items.forEach((item, index) => {
    const current = listNodes[index];
    const next = listNodes[index + 1] || _solidUi.ns.rdf('nil');
    statements.push((0, _rdflib.st)(current, _solidUi.ns.rdf('first'), item, doc));
    statements.push((0, _rdflib.st)(current, _solidUi.ns.rdf('rest'), next, doc));
  });
  return {
    head: listNodes[0],
    statements
  };
}
function buildLanguageStatements(subject, doc, rows) {
  const languageRows = rows.map(row => ({
    name: normalizeText(row.name),
    publicId: normalizeLanguageCode(row.publicId)
  })).filter(row => Boolean(row.publicId));
  if (languageRows.length === 0) return [];
  const entryNodes = languageRows.map(() => (0, _idNodeFactory.createIdNode)(doc));
  const statements = [];
  const rdfList = buildRdfListStatements(entryNodes, doc);
  statements.push((0, _rdflib.st)(subject, _solidUi.ns.schema('knowsLanguage'), rdfList.head, doc));
  statements.push(...rdfList.statements);
  entryNodes.forEach((entryNode, index) => {
    const publicIdNode = (0, _rdflib.sym)(`${LANGUAGE_IANA_NS}${languageRows[index].publicId}`);
    statements.push((0, _rdflib.st)(entryNode, _solidUi.ns.solid('publicId'), publicIdNode, doc));
    if (languageRows[index].name) {
      statements.push((0, _rdflib.st)(publicIdNode, _solidUi.ns.schema('name'), (0, _rdflib.literal)(languageRows[index].name, 'en'), doc));
    }
  });
  return statements;
}
function collectListChainNodes(store, listHead, doc) {
  const visited = new Set();
  const nodes = [];
  let current = listHead;
  while (current) {
    const key = `${current.termType}:${current.value}`;
    if (visited.has(key)) break;
    visited.add(key);
    nodes.push(current);
    const rest = store.any(current, _solidUi.ns.rdf('rest'), null, doc);
    if (!rest || rest.termType === 'NamedNode' && rest.value === _solidUi.ns.rdf('nil').value) {
      break;
    }
    current = rest;
  }
  return nodes;
}
function isPatchFailure(message) {
  const text = (message || '').toLowerCase();
  // Different pods surface PATCH incompatibility with different status codes/messages.
  return text.includes(' on patch ') || text.includes('web error: 501') || text.includes('web error: 405') || text.includes('web error: 400');
}
function isMissingGetRecordError(message) {
  return (message || '').toLowerCase().includes('no record of our http get request for document');
}
function statementKey(statement) {
  return `${statement.subject?.toNT?.() || statement.subject?.value} ${statement.predicate?.toNT?.() || statement.predicate?.value} ${statement.object?.toNT?.() || statement.object?.value} ${statement.why?.toNT?.() || statement.why?.value}`;
}
function sanitizePatchStatements(store, deletions, insertions) {
  const safeDeletions = Array.from(new Map((deletions || []).filter(statement => {
    if (!statement || !statement.subject || !statement.predicate || !statement.object) return false;
    if (store.holds(statement.subject, statement.predicate, statement.object, statement.why)) return true;
    return store.statementsMatching(statement.subject, statement.predicate, statement.object, statement.why).length > 0;
  }).map(statement => [statementKey(statement), statement])).values());
  const safeInsertions = Array.from(new Map((insertions || []).filter(statement => Boolean(statement && statement.subject && statement.predicate && statement.object)).map(statement => [statementKey(statement), statement])).values());
  return {
    safeDeletions,
    safeInsertions
  };
}
async function runPutFallback(store, doc, deletions, insertions) {
  const updater = store.updater;
  const fetcher = store.fetcher;
  if (!updater || typeof updater.serialize !== 'function' || !fetcher || typeof fetcher.webOperation !== 'function') {
    throw new Error('Language updates are not supported by this store updater.');
  }
  const currentStatements = store.statementsMatching(undefined, undefined, undefined, doc).slice();
  const deletionKeys = new Set((deletions || []).map(statement => statementKey(statement)));
  // Rebuild full document state for PUT when patch-oriented flows cannot be used.
  const nextStatements = currentStatements.filter(statement => !deletionKeys.has(statementKey(statement))).concat(insertions || []);
  const contentType = 'text/turtle';
  const body = updater.serialize(doc.value, nextStatements, contentType);
  const response = await fetcher.webOperation('PUT', doc.value, {
    noMeta: true,
    contentType,
    body
  });
  if (!response || response.ok !== true) {
    const status = response?.status || 'unknown';
    throw new Error(`Web error: ${status} on PUT of <${doc.value}>`);
  }

  // PUT bypasses UpdateManager's local-store patching, so apply the same changes locally.
  store.remove(deletions);
  insertions.forEach(statement => {
    store.add(statement.subject, statement.predicate, statement.object, statement.why);
  });
}
async function runUpdateWithDavFallback(store, doc, deletions, insertions) {
  const updater = store.updater;
  if (!updater || typeof updater.update !== 'function') {
    throw new Error('Language updates are not supported by this store updater.');
  }
  const {
    safeDeletions,
    safeInsertions
  } = sanitizePatchStatements(store, deletions, insertions);
  if (safeDeletions.length === 0 && safeInsertions.length === 0) {
    return;
  }
  const tryUpdate = () => new Promise((resolve, reject) => {
    updater.update(safeDeletions, safeInsertions, (_uri, ok, message) => {
      if (ok === true) {
        resolve();
        return;
      }
      reject(new Error(message || 'Failed to save languages'));
    });
  });
  try {
    // Preferred path: let rdflib pick the server-supported update protocol.
    await tryUpdate();
    return;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!isPatchFailure(message) || typeof updater.updateDav !== 'function') {
      throw error;
    }
    if (store.fetcher && typeof store.fetcher.load === 'function') {
      try {
        await store.fetcher.load(doc);
      } catch {
        // Continue; updateDav may still fail and we handle that below.
      }
    }
    try {
      // First fallback for PATCH failures: DAV-style whole-document update.
      await new Promise((resolve, reject) => {
        updater.updateDav(doc, safeDeletions, safeInsertions, (_uri, ok, body) => {
          if (ok === true) {
            resolve();
            return;
          }
          reject(new Error(body || message || 'Failed to save languages'));
        });
      });
    } catch (davError) {
      const davMessage = davError instanceof Error ? davError.message : String(davError);
      if (!isMissingGetRecordError(davMessage)) {
        throw davError;
      }
      // Some stores cannot run updateDav without prior fetch metadata; use direct PUT.
      await runPutFallback(store, doc, safeDeletions, safeInsertions);
    }
  }
}
function mergeLanguageOps(existingRows, languageOps) {
  const byEntryNode = new Map();
  existingRows.forEach(row => {
    byEntryNode.set(row.entryNode, row);
  });
  languageOps.remove.forEach(row => {
    if (!row.entryNode) return;
    byEntryNode.delete(row.entryNode);
  });
  languageOps.update.forEach(row => {
    if (!row.entryNode) return;
    if (!byEntryNode.has(row.entryNode)) return;
    byEntryNode.set(row.entryNode, row);
  });
  languageOps.create.forEach(row => {
    if (!normalizeText(row.publicId)) return;
    byEntryNode.set(`new:${row.name}:${Math.random()}`, {
      ...row,
      publicId: normalizeText(row.publicId),
      proficiency: normalizeText(row.proficiency)
    });
  });
  const dedupedByLanguage = new Map();
  Array.from(byEntryNode.values()).forEach(row => {
    const publicId = normalizeText(row.publicId);
    if (!publicId) return;
    if (!dedupedByLanguage.has(publicId)) {
      dedupedByLanguage.set(publicId, {
        ...row,
        publicId,
        proficiency: normalizeText(row.proficiency)
      });
    }
  });
  return Array.from(dedupedByLanguage.values());
}
async function mutateLanguageEntries(store, subject, languageOps, orderedRows) {
  ensureLanguagePrefix(store);
  const doc = subject.doc();
  const existingRows = (0, _selectors.presentLanguages)(subject, store).map(detail => ({
    name: normalizeText(detail.name),
    publicId: normalizeText(detail.publicId),
    proficiency: normalizeText(detail.proficiency),
    entryNode: detail.entryNode.value,
    status: 'existing'
  }));
  const nextRows = orderedRows && orderedRows.length ? rowsFromOrderedInput(orderedRows) : mergeLanguageOps(existingRows, languageOps);
  const listObjects = store.each(subject, _solidUi.ns.schema('knowsLanguage'), null, doc);
  const existingListHeads = listObjects.filter(node => node.termType === 'BlankNode' || node.termType === 'NamedNode');
  const existingListNodes = Array.from(new Map(existingListHeads.flatMap(node => collectListChainNodes(store, node, doc)).map(node => [`${node.termType}:${node.value}`, node])).values());
  const existingLanguageNodes = Array.from(new Set(listObjects.flatMap(node => (0, _rdfList.expandRdfList)(store, node)).filter(node => node.termType === 'NamedNode' || node.termType === 'BlankNode').map(node => `${node.termType}:${node.value}`))).map(key => {
    const [termType, ...rest] = key.split(':');
    const value = rest.join(':');
    return termType === 'BlankNode' ? store.bnode(value) : store.sym(value);
  });
  const existingPublicIdNodes = Array.from(new Map(existingLanguageNodes.map(node => store.any(node, _solidUi.ns.solid('publicId'), null, doc)).filter(node => Boolean(node && node.termType === 'NamedNode')).map(node => [`${node.termType}:${node.value}`, node])).values());
  const deletions = store.statementsMatching(subject, _solidUi.ns.schema('knowsLanguage'), null, doc);
  existingListNodes.forEach(node => {
    deletions.push(...store.statementsMatching(node, null, null, doc));
  });
  existingLanguageNodes.forEach(node => {
    // Remove old language-entry statements so save always rewrites canonical shape.
    deletions.push(...store.statementsMatching(node, null, null, doc));
  });
  existingPublicIdNodes.forEach(node => {
    // Language labels are attached to the publicId resource (l:xx) and should be removed for deleted rows.
    deletions.push(...store.statementsMatching(node, _solidUi.ns.schema('name'), null, doc));
  });
  const insertions = buildLanguageStatements(subject, doc, nextRows);
  await runUpdateWithDavFallback(store, doc, deletions, insertions);
}
async function processLanguageMutations(store, subject, mutationPlan, orderedRows) {
  try {
    await mutateLanguageEntries(store, subject, mutationPlan, orderedRows);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`${_texts.mutationSaveLanguagesFailedPrefixText} ${message}`);
  }
}